import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { queryClient } from '@/lib/queryClient';

export type AppRole = 'superuser' | 'admin' | 'user' | 'viewer';

export function useAuth() {
  const lastUserIdRef = useRef<string | null>(null);

  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<AppRole>('viewer');
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);

  // Role checks - all scoped to user's company
  const isSuperuser = role === 'superuser'; // Highest role in company
  const isAdmin = role === 'superuser' || role === 'admin'; // Can manage company users
  const isUser = role === 'superuser' || role === 'admin' || role === 'user'; // Can perform actions
  const isViewer = true; // All authenticated users can view

  useEffect(() => {
    const clearCachedDataIfUserChanged = (nextUserId: string | null) => {
      // Only clear if we HAD a previous user and switched to a different one
      // Don't clear on initial load (when lastUserIdRef.current is null)
      if (lastUserIdRef.current !== null && lastUserIdRef.current !== nextUserId) {
        // Prevent cross-account "stale UI" where cached queries from the previous
        // session briefly appear after switching users.
        queryClient.clear();
      }
      lastUserIdRef.current = nextUserId;
    };

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        const nextUserId = session?.user?.id ?? null;
        clearCachedDataIfUserChanged(nextUserId);

        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Defer role check with setTimeout to avoid deadlock
        if (session?.user) {
          setTimeout(() => {
            fetchUserRole(session.user.id);
            fetchUserProfile(session.user.id);
          }, 0);
        } else {
          setRole('viewer');
          setCompanyId(null);
          setCompanyName(null);
          setDisplayName(null);
          setProfileId(null);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      const nextUserId = session?.user?.id ?? null;
      clearCachedDataIfUserChanged(nextUserId);

      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user) {
        fetchUserRole(session.user.id);
        fetchUserProfile(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserRole = async (userId: string) => {
    const { data } = await supabase
      .rpc('get_user_role', { _user_id: userId });
    
    setRole((data as AppRole) || 'viewer');
  };

  const fetchUserProfile = async (userId: string) => {
    const { data } = await supabase
      .from('profiles')
      .select('id, company_id, display_name, companies(name)')
      .eq('user_id', userId)
      .maybeSingle();
    
    setProfileId((data as any)?.id ?? null);
    setCompanyId((data as any)?.company_id ?? null);
    setDisplayName((data as any)?.display_name ?? null);
    setCompanyName(((data as any)?.companies as any)?.name ?? null);
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const checkEmailAllowed = async (email: string) => {
    const { data, error } = await supabase
      .rpc('check_email_allowed', { _email: email });
    
    if (error || !data || data.length === 0) {
      return { allowed: false, companyId: null, companyName: null, hasTempPassword: false };
    }
    
    return { 
      allowed: data[0].is_allowed, 
      companyId: data[0].company_id, 
      companyName: data[0].company_name,
      hasTempPassword: data[0].has_temp_password ?? false,
    };
  };

  const verifyInviteCode = async (email: string, inviteCode: string) => {
    const { data, error } = await supabase
      .rpc('verify_invite_password', { _email: email, _password: inviteCode });
    
    if (error) return false;
    return data === true;
  };

  const signUp = async (email: string, password: string, displayName?: string, inviteCode?: string) => {
    // First check if email is whitelisted
    const emailCheck = await checkEmailAllowed(email);
    
    if (!emailCheck.allowed) {
      return { 
        error: { message: 'Your email has not been authorized. Please contact your administrator to request access.' },
        companyId: null,
        companyName: null
      };
    }

    // If the email has a temp password set, verify the invite code
    if (emailCheck.hasTempPassword) {
      if (!inviteCode) {
        return {
          error: { message: 'An invite code is required to sign up with this email. Please enter the code provided by your administrator.' },
          companyId: null,
          companyName: null,
          needsInviteCode: true
        };
      }
      
      const isValidCode = await verifyInviteCode(email, inviteCode);
      if (!isValidCode) {
        return {
          error: { message: 'Invalid invite code. Please check the code provided by your administrator.' },
          companyId: null,
          companyName: null
        };
      }
    }

    const redirectUrl = `${window.location.origin}/app`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: { 
          display_name: displayName,
          company_id: emailCheck.companyId
        }
      }
    });

    // If signup successful, create profile and mark email as used
    if (!error && data.user) {
      await supabase
        .from('profiles')
        .upsert({
          user_id: data.user.id,
          email: email,
          display_name: displayName || null,
          company_id: emailCheck.companyId
        }, {
          onConflict: 'user_id'
        });
      
      // Mark email as used
      await supabase.rpc('mark_email_used', { _email: email });
    }

    return { error, companyId: emailCheck.companyId, companyName: emailCheck.companyName };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  return {
    user,
    session,
    loading,
    role,
    isSuperuser,
    isAdmin,
    isUser,
    isViewer,
    companyId,
    companyName,
    displayName,
    profileId,
    checkEmailAllowed,
    verifyInviteCode,
    signIn,
    signUp,
    signOut,
  };
}
